// @section 1 BriefingCard — Daily Ops Review card.
// Published by the 6AM scheduled session → hq_core.briefings.
// Extracted from routes/MyDay.tsx 2026-06-06 when the Daily Ops Review moved
// to its own /daily-ops-review route; this card is now shared between the
// standalone page and any future inline previews.

// @subsection 1.1 Types

export type Briefing = {
  briefing_date?: string;
  headline?: string;
  summary?: string | null;
  pipeline_status?: 'ok' | 'failed' | 'stale' | string;
  opportunities?: Array<{ title?: string; detail?: string; action?: string }>;
  report_path?: string | null;
};

// @subsection 1.2 Component

export function BriefingCard({ b }: { b: Briefing }) {
  const status = (b?.pipeline_status || 'unknown') as string;
  const badge =
    status === 'ok' ? 'bg-ok/15 text-ok'
    : status === 'failed' ? 'bg-stall/15 text-stall'
    : status === 'stale' ? 'bg-watch/15 text-watch'
    : 'bg-ink-800 text-muted';
  const opps = Array.isArray(b?.opportunities) ? b.opportunities : [];
  return (
    <div className="mb-8 rounded-xl border border-ink-800 bg-gradient-to-br from-ink-900 to-ink-900/40 p-6 ring-1 ring-accent-500/10">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-[11px] uppercase tracking-[2px] text-muted font-semibold">📋 Daily Ops Review</span>
          <span className="text-[11px] text-muted">{b?.briefing_date}</span>
        </div>
        <span className={`text-[10px] uppercase tracking-[1px] font-bold px-2 py-0.5 rounded ${badge}`}>
          pipeline {status}
        </span>
      </div>
      <div className="text-xl font-semibold text-paper-100 leading-snug">{b?.headline}</div>
      {b?.summary && <p className="text-sm text-muted mt-1">{b.summary}</p>}
      {opps.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold">Opportunities</div>
          {opps.slice(0, 6).map((o, i) => (
            <div key={i} className="flex gap-2 text-sm">
              <span className="text-accent-400 font-semibold flex-shrink-0">{i + 1}.</span>
              <div className="min-w-0">
                <span className="text-paper-100 font-medium">{o?.title}</span>
                {o?.detail && <span className="text-muted"> {o.detail}</span>}
                {o?.action && <div className="text-[12px] text-accent-400/80 mt-0.5">→ {o.action}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {b?.report_path && (
        <div className="text-[11px] text-muted mt-4">Full report: <code className="text-paper-100/70">{b.report_path}</code></div>
      )}
    </div>
  );
}
